import { Controller } from '@hotwired/stimulus'
import imagesLoaded from 'imagesloaded'
import { useWindowResize } from 'stimulus-use'
import { Masonry } from '../utils'
import { DRAGGED, PRODUCT_PREFIX, SECTION_PREFIX } from '../utils/grid_items'

export default class extends Controller {
  static targets = ['displayZone', 'loader']

  static values = {
    displayMode: { type: String, default: 'masonry' },
    imageLoadTimeout: { type: Number, default: 6000 }, // 6 seconds default timeout
    uninitializedAttribute: { type: String, default: 'data-grid-uninitialized' },
  }

  connect() {
    this.windowWidth = window.innerWidth
    if (this.#isMasonry) {
      this.masonry = new Masonry(this.displayZoneTarget)
      useWindowResize(this)
      this.stampImageSizes = this.#stampImageSizes.bind(this)
      document.addEventListener('turbo:before-cache', this.stampImageSizes)
    }
    this.#initGrid()
  }

  disconnect() {
    if (this.stampImageSizes) document.removeEventListener('turbo:before-cache', this.stampImageSizes)
    this.masonry?.disconnect()
    this.masonry = undefined
  }

  appendBeaconTargetConnected(beaconElement) {
    this.#dropDuplicates()
    this.#appended()
    beaconElement.remove()
  }

  layoutBeaconTargetConnected(beaconElement) {
    this.layout()
    beaconElement.remove()
  }

  windowResize({ width }) {
    // useWindowResize fires callback on init.
    // @see https://github.com/stimulus-use/stimulus-use/blob/main/src/use-window-resize/use-window-resize.ts#L29
    // So we avoid callback execution when no resize.
    if (this.windowWidth === width) return

    this.layout()
    this.windowWidth = width
  }

  layout(event) {
    this.masonry?.layout()
  }

  // Synchronous variant, for Sortable's `onChange` (see Masonry#layoutNow).
  relayout(event) {
    this.masonry?.layoutNow()
  }

  #initGrid() {
    // La première page de la grille des réservées peut aussi reprendre une carte de la grille principale.
    this.#dropDuplicates()
    const items = this.displayZoneTarget.querySelectorAll(`[${this.uninitializedAttributeValue}]`)

    // Images déjà en cache navigateur : révélation synchrone, avant le premier
    // paint — le shimmer n'est jamais affiché (navigation Turbo « chaude »).
    if (this.#allImagesComplete(items)) {
      this.loaderTargets.forEach((target) => target.remove())
      this.#manageItems(items)
      // Microtâche : à cet instant les contrôleurs frères (infinite-pagination)
      // ne sont pas encore connectés — un dispatch synchrone partirait dans le
      // vide. Les microtâches précèdent le paint, la révélation reste invisible.
      queueMicrotask(() => this.dispatch('rendered'))
      return
    }

    // Handle items after images are loaded or timeout occurs
    this.#loadImages(items).then(() => {
      // Remove loaders BEFORE layout: they have no data-masonry-break attribute, so
      // Masonry would otherwise count them as regular items in the column layout.
      this.loaderTargets.forEach((target) => target.remove())

      this.#manageItems(items)
      this.dispatch('rendered')
    })
  }

  // `img.complete` est vrai aussi pour une image en erreur — même contrat que
  // le `always` d'imagesLoaded utilisé ci-dessous.
  #allImagesComplete(items) {
    return [...items].every((item) => [...item.querySelectorAll('img')].every((img) => img.complete))
  }

  // Filet : une page qui renvoie une carte ou un titre déjà dans la grille (déplacé depuis par une
  // autre session ou un rangement) n'en fait pas un doublon d'id. C'est la copie qui vient d'arriver
  // qui reste : elle est à sa place d'aujourd'hui, sous sa section, et les cartes qui la suivent
  // dans la page avec elle. L'ancienne, périmée, part. En masonry, le trou qu'elle laisse parmi
  // les cartes déjà placées se referme seul : le ResizeObserver de Masonry voit une carte retirée
  // passer à 0 de haut et recalcule la grille (spec/features/paginating_a_list_spec.rb).
  // De même d'une grille à l'autre : une envie réservée depuis son affichage dans la grille
  // principale arrive aussi dans celle des réservées (lists/reserved_products), qui la garde.
  // Sauf pendant un glisser en mode Organiser (#462) : la carte ou le titre saisi, et la copie qui
  // suit le doigt, restent — retirés, le glisser s'arrêtait net. C'est alors la copie qui vient
  // d'arriver dans cette grille qui part : la place de l'élément saisi ne sera connue qu'au lâcher.
  #dropDuplicates() {
    const held = new Set(
      Array.from(this.displayZoneTarget.querySelectorAll(`:scope > .${DRAGGED}[id]`), (item) => item.id)
    )
    const latest = new Map()
    for (const item of this.displayZoneTarget.children) if (item.id && !held.has(item.id)) latest.set(item.id, item)

    for (const item of document.querySelectorAll(`[id^="${PRODUCT_PREFIX}"], [id^="${SECTION_PREFIX}"]`)) {
      if (item.classList.contains(DRAGGED)) continue

      const kept = latest.get(item.id)
      const arrivedBesideHeld = held.has(item.id) && item.parentElement === this.displayZoneTarget
      if (arrivedBesideHeld || (kept && kept !== item)) item.remove()
    }
  }

  #appended() {
    const items = this.displayZoneTarget.querySelectorAll(`[${this.uninitializedAttributeValue}]`)

    // Handle appended items after images are loaded or timeout occurs
    this.#loadImages(items).then(() => {
      this.#manageItems(items)
      this.dispatch('rendered')
    })
  }

  // La copie de la page gardée par Turbo (retour arrière, web comme app) est réaffichée
  // avant que ses images ne reviennent : sans taille déclarée, chaque carte y aurait sa
  // hauteur d'image vide, puis grandirait — la grille se recalculait sous les yeux. Les
  // images sont toutes chargées quand on quitte la page : leur taille réelle est écrite
  // dessus, la copie restaurée a d'emblée ses hauteurs définitives.
  // Repli seulement : une couverture stockée arrive déjà avec sa taille, connue en base
  // (Product#cover_dimensions). Restent celles qui ne le sont pas — URL distante pas encore
  // importée, image par défaut, vitrine.
  #stampImageSizes() {
    for (const img of this.displayZoneTarget.querySelectorAll('img')) {
      if (!img.complete || !img.naturalWidth || img.hasAttribute('width')) continue

      img.setAttribute('width', img.naturalWidth)
      img.setAttribute('height', img.naturalHeight)
    }
  }

  get #isMasonry() {
    return this.displayModeValue === 'masonry'
  }

  #manageItems(items) {
    for (const item of items) {
      item.removeAttribute(this.uninitializedAttributeValue)
    }
    // Même sans carte à révéler (retour sur une page restaurée depuis le cache Turbo) :
    // les marges du masonry y sont déjà et appended() ne déplace rien, mais c'est lui
    // qui met les cartes sous l'observateur de taille de cette nouvelle instance — une
    // image qui finit d'arriver doit pouvoir faire recalculer la grille.
    this.masonry?.appended()
  }

  // Create a promise that resolves when images are loaded or times out
  #loadImages(items) {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.warn('Images loading timed out after', this.imageLoadTimeoutValue, 'ms')
        resolve()
        this.layout()
      }, this.imageLoadTimeoutValue)

      imagesLoaded(items).on('always', () => {
        clearTimeout(timeout)
        resolve()
      })
    })
  }
}
